import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1200&q=80',
];

const CompactCareerCard = ({ careerCard, careerIndex, sectionKey, isAdmin, editMode, openActionPath }) => (
  <Box
    key={`${sectionKey}-career-card-${careerIndex}`}
    sx={{
      p: 1.25,
      borderRadius: 0,
      background: 'rgba(15,23,42,0.03)',
      border: '1px solid rgba(15, 23, 42, 0.08)',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.8,
    }}
  >
    <Typography sx={{ fontWeight: 800, fontSize: '0.86rem', color: '#0F172A' }}>
      {careerCard.title || `Role ${careerIndex + 1}`}
    </Typography>
    <Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap' }}>
      {careerCard.department ? (
        <Box sx={{ px: 0.7, py: 0.25, borderRadius: 0, background: 'rgba(0,195,137,0.12)' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#059669' }}>
            {careerCard.department}
          </Typography>
        </Box>
      ) : null}
      {careerCard.location ? (
        <Box sx={{ px: 0.7, py: 0.25, borderRadius: 0, background: 'rgba(14,165,233,0.12)' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#BE123C' }}>
            {careerCard.location}
          </Typography>
        </Box>
      ) : null}
      {careerCard.employmentType ? (
        <Box sx={{ px: 0.7, py: 0.25, borderRadius: 0, background: 'rgba(15,23,42,0.14)' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0F172A' }}>
            {careerCard.employmentType}
          </Typography>
        </Box>
      ) : null}
    </Stack>
    {careerCard.summary ? (
      <Typography sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.8rem' }}>
        {careerCard.summary}
      </Typography>
    ) : null}
    <Button
      size="small"
      variant="outlined"
      sx={{ width: 'fit-content' }}
      disabled={!careerCard.applyPath}
      onClick={() => {
        if (!(isAdmin && editMode) && careerCard.applyPath) {
          openActionPath(careerCard.applyPath);
        }
      }}
    >
      Apply
    </Button>
  </Box>
);

const PremiumCareerCard = ({ careerCard, careerIndex, sectionKey, careersLayout, isAdmin, editMode, onApply }) => {
  const cardImage = careerCard.image || FALLBACK_IMAGES[careerIndex % FALLBACK_IMAGES.length];
  const isVerticalLayout = careersLayout === 'vertical';

  return (
    <Box
      key={`${sectionKey}-career-card-${careerIndex}`}
      sx={{
        borderRadius: 0,
        overflow: 'hidden',
        border: '1px solid rgba(15, 23, 42, 0.08)',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,250,255,0.96) 100%)',
        boxShadow: '0 14px 34px rgba(15,23,42,0.1)',
        display: 'grid',
        gridTemplateColumns: isVerticalLayout ? { xs: '1fr', md: 'minmax(0, 1.05fr) minmax(0, 1fr)' } : '1fr',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 220, md: isVerticalLayout ? 270 : 235 },
          borderRight: isVerticalLayout ? { md: '1px solid rgba(15,23,42,0.08)' } : 'none',
        }}
      >
        <Box
          component="img"
          src={cardImage}
          alt={careerCard.title || `Role ${careerIndex + 1}`}
          sx={{
            width: '100%',
            height: '100%',
            minHeight: { xs: 220, md: isVerticalLayout ? 270 : 235 },
            objectFit: 'cover',
            display: 'block',
          }}
        />
        {careerCard.department ? (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              px: 1,
              py: 0.35,
              borderRadius: 0,
              background: 'rgba(13, 36, 69, 0.86)',
              backdropFilter: 'blur(6px)',
            }}
          >
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#FFFFFF' }}>
              {careerCard.department}
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Stack spacing={1.1} sx={{ p: { xs: 1.25, md: 1.5 } }}>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.98rem', md: '1.08rem' }, color: '#0F172A' }}>
          {careerCard.title || `Role ${careerIndex + 1}`}
        </Typography>

        <Stack direction="row" spacing={0.7} sx={{ flexWrap: 'wrap' }}>
          {careerCard.location ? (
            <Box
              sx={{
                px: 0.82,
                py: 0.28,
                borderRadius: 0,
                background: 'rgba(37,99,235,0.1)',
                border: '1px solid rgba(37,99,235,0.2)',
              }}
            >
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1D4ED8' }}>
                {careerCard.location}
              </Typography>
            </Box>
          ) : null}
          {careerCard.employmentType ? (
            <Box
              sx={{
                px: 0.82,
                py: 0.28,
                borderRadius: 0,
                background: 'rgba(16,185,129,0.12)',
                border: '1px solid rgba(16,185,129,0.22)',
              }}
            >
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#047857' }}>
                {careerCard.employmentType}
              </Typography>
            </Box>
          ) : null}
        </Stack>

        {careerCard.summary ? (
          <Typography
            sx={{
              color: 'rgba(15,23,42,0.74)',
              lineHeight: 1.6,
              fontSize: '0.82rem',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {careerCard.summary}
          </Typography>
        ) : null}

        <Button
          size="small"
          variant="contained"
          sx={{
            width: 'fit-content',
            borderRadius: 0,
            px: 1.5,
            textTransform: 'none',
            fontWeight: 700,
            background: 'linear-gradient(90deg, #0B3A67 0%, #1D4ED8 100%)',
            boxShadow: '0 8px 18px rgba(13,56,117,0.24)',
          }}
          onClick={() => {
            if (!(isAdmin && editMode)) {
              onApply(careerCard);
            }
          }}
        >
          Apply Now
        </Button>
      </Stack>
    </Box>
  );
};

const CareerOpeningsSection = ({
  section,
  sectionKey,
  careerCards,
  careersLayout,
  careersColumns,
  slug,
  isAdmin,
  editMode,
  openActionPath,
  onApply,
  renderHtmlTextBlock,
}) => (
  <Stack spacing={1} sx={{ mt: 1 }}>
    {section.body
      ? renderHtmlTextBlock(section.body, {
          color: 'text.secondary',
          lineHeight: 1.65,
          fontSize: { xs: '0.8rem', md: '0.86rem' },
        })
      : null}
    {careerCards.length ? (
      <Box
        sx={{
          display: 'grid',
          gap: 1.1,
          gridTemplateColumns:
            careersLayout === 'vertical'
              ? 'repeat(1, minmax(0, 1fr))'
              : {
                  xs: 'repeat(1, minmax(0, 1fr))',
                  sm: `repeat(${Math.min(2, careersColumns)}, minmax(0, 1fr))`,
                  md: `repeat(${careersColumns}, minmax(0, 1fr))`,
                },
        }}
      >
        {careerCards.map((careerCard, careerIndex) => {
          const isCareersPage = slug === 'careers';
          if (!isCareersPage) {
            return (
              <CompactCareerCard
                key={`${sectionKey}-compact-${careerIndex}`}
                careerCard={careerCard}
                careerIndex={careerIndex}
                sectionKey={sectionKey}
                isAdmin={isAdmin}
                editMode={editMode}
                openActionPath={openActionPath}
              />
            );
          }

          return (
            <PremiumCareerCard
              key={`${sectionKey}-premium-${careerIndex}`}
              careerCard={careerCard}
              careerIndex={careerIndex}
              sectionKey={sectionKey}
              careersLayout={careersLayout}
              isAdmin={isAdmin}
              editMode={editMode}
              onApply={onApply}
            />
          );
        })}
      </Box>
    ) : (
      <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }}>
        Add career rows in editor to show openings.
      </Typography>
    )}
  </Stack>
);

export default CareerOpeningsSection;
